<template>
  <div>
    <!-- Display debugging info in development mode -->
    <div v-if="false" style="font-size: 10px; color: #999;">
      Player: {{ playerId }}, Fav: {{ isFavorite ? 'Yes' : 'No' }}
    </div>
    
    <button 
      class="favorite-button" 
      :class="{ 'favorite': isFavorite }" 
      @click="toggleFavorite"
      :data-player-id="playerId">
      <span class="star-icon">
        <span class="vh">
          {{ isFavorite ? removeFromFavoritesText : addToFavoritesText }}
        </span>
        <!-- Use the star_full SVG with conditional class -->
        <span v-if="starFull" v-html="starFull" :class="{ 'filled': isFavorite }"></span>
        <span v-else class="fallback-star" :class="{ 'filled': isFavorite }">
          {{ isFavorite ? '⭐' : '★' }}
        </span>
      </span>
    </button>
  </div>
</template>

<script>
import { db } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove, serverTimestamp, onSnapshot } from 'firebase/firestore';

export default {
  name: 'FavoriteButton',
  props: {
    playerId: {
      type: [String, Number],
      required: true
    },
    userId: {
      type: [String, Number],
      required: true
    },
    initialIsFavorite: {
      type: Boolean,
      default: false
    },
    addToFavoritesText: {
      type: String,
      default: 'Add to favorites'
    },
    removeFromFavoritesText: {
      type: String,
      default: 'Remove from favorites'
    },
    starEmpty: {
      type: String,
      default: ''
    },
    starFull: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isFavorite: this.initialIsFavorite,
      isProcessing: false,
      playerIdString: String(this.playerId),
      userIdString: String(this.userId),
      unsubscribe: null
    };
  },
  mounted() {
    this.setupRealTimeListener();
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  methods: {
    // Helper to ensure IDs are always strings
    ensureString(value) {
      return value !== null && value !== undefined ? String(value) : '';
    },
    
    setupRealTimeListener() {
      if (!this.userIdString) return;
      
      try {
        const userDocRef = doc(db, 'userData', `wp_${this.userIdString}`);
        
        // Set up real-time listener
        this.unsubscribe = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            const favoritePlayers = userData.favoritePlayers || [];
            // Convert all IDs to strings for comparison
            const favoritePlayerIds = favoritePlayers.map(id => this.ensureString(id));
            this.isFavorite = favoritePlayerIds.includes(this.playerIdString);
          }
        }, (error) => {
          console.error('Error in real-time listener:', error);
        });
      } catch (error) {
        console.error('Error setting up real-time listener:', error);
      }
    },
    
    async toggleFavorite() {
      if (!this.userIdString || this.isProcessing) return;
      
      this.isProcessing = true;
      
      try {
        const userDocRef = doc(db, 'userData', `wp_${this.userIdString}`);
        
        // Get current document
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists()) {
          // Update existing document using arrayUnion/arrayRemove
          // These are atomic operations that handle the add/remove logic for us
          await updateDoc(userDocRef, {
            favoritePlayers: this.isFavorite 
              ? arrayRemove(this.playerIdString) 
              : arrayUnion(this.playerIdString),
            favoritePlayersLastUpdate: serverTimestamp()
          });
        } else {
          // Create new document with initial data
          await setDoc(userDocRef, {
            favoritePlayers: [this.playerIdString],
            favoritePlayersLastUpdate: serverTimestamp()
          });
        }
        
        // Toggle local state for immediate feedback
        // The real-time listener will update it with the server state
        this.isFavorite = !this.isFavorite;
      } catch (error) {
        console.error('Error updating favorite status:', error);
      } finally {
        this.isProcessing = false;
      }
    }
  }
};
</script>

<style scoped>
.fallback-star {
  font-size: 18px;
  line-height: 1;
}
.fallback-star.filled {
  color: var(--main--accent);
}
/* Styling for the SVG */
span:not(.filled) svg path {
  fill: transparent !important;
  stroke: var(--main--accent) !important;
  stroke-width: 1px !important;
}
.filled svg path {
  fill: var(--main--accent) !important;
  stroke: var(--main--accent) !important;
}
</style> 